/**
 * Shapes shared with the PHP side.
 *
 * These mirror theme.json and the public game state. Typing them is the main
 * practical reason for adopting TypeScript here: the 2025 build kept the same
 * information as loose objects, and misc-note.txt exists because a room rename
 * had to be mirrored by hand in eight places. Here a mistyped field is a build
 * error instead of a blank board.
 */

export const Tile = {
  Void: 0,
  Corridor: 1,
  Room: 2,
  Door: 3,
} as const;

export type TileType = (typeof Tile)[keyof typeof Tile];

export interface Point {
  x: number;
  y: number;
}

export interface ThemeRoom {
  id: string;
  name: string;
  isCard: boolean;
  anchor: Point;
  labelOffset: Point;
  cells: Point[];
  doors: Point[];
  passageTo: string | null;
  cardImage: string | null;
}

export interface ThemeSuspect {
  id: string;
  name: string;
  shortName: string;
  token: string;
  color: string;
  start: Point;
  cardImage: string | null;
  tokenImage?: string | null;
}

export interface ThemeWeapon {
  id: string;
  name: string;
  token: string;
  cardImage: string | null;
}

export interface Theme {
  schemaVersion: number;
  id: string;
  name: string;
  board: {
    cols: number;
    rows: number;
    tiles: TileType[][];
    centerpiece?: { label: string; anchor: Point };
    /** Relative to the theme's own folder, same as cardImage. Optional -- a
     * theme with no art for the board yet just keeps the flat tile colours. */
    underlayImage?: string;
  };
  rooms: ThemeRoom[];
  suspects: ThemeSuspect[];
  weapons: ThemeWeapon[];
  strings: Record<string, string>;
  palette: Record<string, string>;
}

export type Phase = 'lobby' | 'rolling' | 'moving' | 'disproving' | 'gameover';

export interface Seat {
  id: string;
  uid: string | null;
  name: string;
  shortName: string;
  /** What to call the person here. Null for bots, and for seats taken
   *  before labels existed -- fall back to the character's name. */
  label: string | null;
  x: number;
  y: number;
  hasLost: boolean;
  isAccusing: boolean;
  isBot: boolean;
  botDifficulty: string | null;
}

export interface Suggestion {
  suspect: string;
  weapon: string;
  room: string;
  suggester: number;
  /** Seat index (as a string key) to whether they could refute. */
  answers: Record<string, boolean>;
}

export interface PublicState {
  themeId: string;
  phase: Phase;
  players: Seat[];
  turnIndex: number;
  disproverIndex: number;
  currentSuggestion: Suggestion | null;
  currentSteps: number;
  rollsCount: number;
  winner: number | null;
  playerCardCounts: number[];
  lastAction: string;
  prevAction: string;
  createdAt?: number;
  updatedAt?: number;
  /** Accumulated thinking time per seat, in seconds. */
  swTimes?: number[];
  /** When the running clock started, as a unix timestamp. */
  swActiveAt?: number;
  swActivePlayer?: number;
  /** Only present once the game is over. */
  caseFile?: { suspect: string; weapon: string; room: string };
}

export interface GameEvent {
  type: string;
  timestamp: number;
  [key: string]: unknown;
}

export type NotepadMode = 'manual' | 'smart' | 'max';

/**
 * What the algorithm can say about one seat holding one card.
 *
 *   has      definitely holds it
 *   not      definitely does not
 *   maybe    said "No! (Has Card)" to a suggestion containing it, and which of
 *            the three they meant is still unsettled -- the notepad's "??"
 *   unknown  nothing known either way
 */
export type CellState = 'has' | 'not' | 'maybe' | 'unknown';

export interface NotepadRow {
  cardId: CardId;
  name: string;
  /** Deduced to be in the case file: the gold row. */
  solved: boolean;
  cells: CellState[];
}

export interface NotepadGrid {
  solved: boolean;
  progress: number;
  sections: { type: string; label: string; rows: NotepadRow[] }[];
}

export interface GameSnapshot {
  gameId: string;
  public: PublicState;
  events: GameEvent[];
  /** Null when watching rather than playing. */
  seat: number | null;
  hand: string[];
  /** Absent for spectators and in the lobby. */
  notepad?: { smart: NotepadGrid; max: NotepadGrid };
  /** This player's own marks: "cardId|seat" => symbol. */
  notes?: Record<string, 'card' | 'cross' | 'query' | 'flag'>;
}

export interface GameSummary {
  gameId: string;
  themeId: string;
  phase: Phase;
  /** `label` is "you" for your own seat -- the server decides which one that is. */
  players: {
    id: string;
    name: string;
    label: string | null;
    isBot: boolean;
    botDifficulty: string | null;
    /** The character's glyph, resolved from the theme pack by the server. */
    token: string | null;
  }[];
  seats: number;
  createdAt: number | null;
  updatedAt: number;
  winner: number | null;
  /** Whether the signed-in player holds a seat here. Decides who may delete it. */
  mine: boolean;
  /** Human players other than you, for the delete confirmation. Never uids. */
  otherHumans: string[];
}

export interface Bootstrap {
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    appId: string;
    databaseURL: string;
  };
  gamesBranch: string;
  summaryBranch: string;
  /** Every installed theme pack, for the chooser on the lobby. */
  themes: { id: string; name: string }[];
  /**
   * The bots each theme offers, keyed by theme id. Editions differ: the family
   * board offers a Gambler that the lab board does not, because accusing early
   * only pays at a table of matched players.
   */
  /** `tagline` is the short one for the picker tiles; `blurb` the long one for
   *  the dialog that fills a single named seat. */
  bots: Record<string, { id: string; label: string; tagline: string; blurb: string }[]>;
  defaultTheme: string;
  minPlayers: number;
  maxLobbies: number;
  /** Seconds a human gets to answer a suggestion before the server answers for them. */
  answerGrace: number;
  uid: string | null;
}

export interface Identity {
  signedIn: boolean;
  email?: string;
  /** Whether this address may manage who is on each theme's list. */
  isAdmin?: boolean;
  uid?: string;
  firebaseToken?: string;
  expiresIn?: number;
}

/** Namespaced card id, e.g. "suspect:plum". */
export type CardId = string;

export function cardType(id: CardId): 'suspect' | 'weapon' | 'room' {
  return id.split(':', 1)[0] as 'suspect' | 'weapon' | 'room';
}

export function cardRef(id: CardId): string {
  const i = id.indexOf(':');
  return i === -1 ? id : id.slice(i + 1);
}
